import {DocumentActionTypes, IAStreamActionTypes} from "../Actions/actionTypes";
import {sortByPage} from "../utils/orderCriteria";
import {
    normalizeFastGlobalObservation,
    normalizeFastGlobalThinking,
    FAST_GLOBAL_BATCH_EVENT_SCHEMA,
} from "../utils/fastGlobalStream";
import {
    getReferenceReviewScope,
    normalizeReferenceReviewSnapshotPayload,
} from "../Scenes/stage-review/reference-review/referenceReviewProjection";

// ── PR#3.4.1 : normalisation du champ enrichment dans chaque commentaire ────
// Version sentinel : garantit qu'on ne ré-initialise pas un enrichment déjà actif
const ENRICHMENT_VERSION = 'comment_enrichment_v1';
const ANALYSIS_REASONING_MAX_MESSAGES = 6;
const ANALYSIS_REASONING_MESSAGES = {
    fr: {
        read: 'Lecture des clauses pertinentes.',
        verify: 'Verification des passages sources.',
        compare: 'Comparaison des clauses reliees.',
        consistency: 'Controle de coherence du contrat.',
        prepare: "Preparation des points d'attention.",
    },
    en: {
        read: 'Reading relevant clauses.',
        verify: 'Verifying source passages.',
        compare: 'Comparing related clauses.',
        consistency: 'Checking contract consistency.',
        prepare: 'Preparing attention points.',
    },
};
function normalizeAnalysisReasoningLanguage(language) {
    const folded = String(language || '').toLowerCase().trim();
    return folded.startsWith('en') || folded.startsWith('ang') ? 'en' : 'fr';
}

function shouldFallbackAnalysisReasoning(message, language) {
    if (!message) return true;
    if (message.length > 420) return true;
    if (message.length < 40 && /(^|\s)(art|article|sec|section)\.?$/i.test(message)) return true;
    if (message.length < 40 && /\b\d+\)\s*(art|article)\.?$/i.test(message)) return true;
    if (message.length < 40 && /\b\d+\s*-\s*$/i.test(message)) return true;

    return false;
}

function normalizeAnalysisReasoningMessage(value, language = 'fr') {
    const message = String(value || '').replace(/\s+/g, ' ').trim();
    if (!message) return '';
    const normalizedLanguage = normalizeAnalysisReasoningLanguage(language);
    const targetMessages = Object.values(ANALYSIS_REASONING_MESSAGES[normalizedLanguage]);
    const otherLanguage = normalizedLanguage === 'fr' ? 'en' : 'fr';
    const otherMessages = Object.values(ANALYSIS_REASONING_MESSAGES[otherLanguage]);
    if (targetMessages.includes(message)) return message;
    if (otherMessages.includes(message)) {
        const index = otherMessages.indexOf(message);
        return targetMessages[index] || targetMessages[0] || '';
    }
    if (!shouldFallbackAnalysisReasoning(message, language)) return message;
    const catalog = ANALYSIS_REASONING_MESSAGES[normalizedLanguage];
    const folded = message.toLowerCase();
    if (/(quote|citation|exact|source|passage)/.test(folded)) {
        return catalog.verify;
    }
    if (/(compare|comparison|comparaison|align|coherence|version|reference)/.test(folded)) {
        return catalog.compare;
    }
    if (/(risk|risque|severity|niveau|liability|responsabilit|termination|resiliation)/.test(folded)) {
        return catalog.consistency;
    }
    if (/(comment|card|recommendation|action|recommandation)/.test(folded)) {
        return catalog.prepare;
    }
    return catalog.read;
}

function normalizeRiskLevelValue(risk) {
    const raw = risk && typeof risk === 'object'
        ? risk.level || risk.risk || risk.severity
        : risk;
    const value = String(raw ?? '').trim().toLowerCase();
    const aliases = {
        critique: 'critical',
        eleve: 'high',
        '\u00e9lev\u00e9': 'high',
        haute: 'high',
        moyen: 'medium',
        moyenne: 'medium',
        faible: 'low',
    };
    return aliases[value] || value || null;
}

function getRiskRationaleValue(comment) {
    const risk = comment?.risk;
    const fromRisk = risk && typeof risk === 'object'
        ? risk.rationale || risk.reason || risk.why
        : null;
    const fromQuickView = comment?.quick_view && typeof comment.quick_view === 'object'
        ? comment.quick_view.risk_rationale || comment.quick_view.riskRationale
        : null;
    const text = String(fromRisk || comment?.risk_rationale || comment?.riskReason || fromQuickView || '')
        .replace(/\s+/g, ' ')
        .trim();
    return text || null;
}

/**
 * Garantit que chaque commentaire possède un champ `enrichment` normalisé.
 * - Si `enrichment.version` est déjà 'comment_enrichment_v1' → pas de modification.
 * - Si le commentaire possède un `detailed_view` initial (venant du backend) → seed comme 'done'.
 * - Sinon → seed comme 'idle'.
 */
function normalizeCommentEnrichment(c) {
    if (!c) return c;
    const riskRationale = getRiskRationaleValue(c);
    const riskLevel = normalizeRiskLevelValue(c.risk ?? c.quick_view?.risk);
    const quickView = c.quick_view && typeof c.quick_view === 'object'
        ? {
            ...c.quick_view,
            ...(riskRationale && !c.quick_view.risk_rationale
                ? { risk_rationale: riskRationale }
                : {}),
        }
        : c.quick_view;
    const normalizedComment = {
        ...c,
        risk: riskLevel || c.risk,
        ...(riskRationale ? { risk_rationale: riskRationale } : {}),
        quick_view: quickView,
    };
    const hasDetailedView = c.detailed_view && typeof c.detailed_view === 'object';
    if (c.enrichment?.version === ENRICHMENT_VERSION) {
        const enrichmentHasDetailedView =
            c.enrichment?.detailed_view && typeof c.enrichment.detailed_view === 'object';

        if (hasDetailedView && !enrichmentHasDetailedView) {
            return {
                ...normalizedComment,
                enrichment: {
                    ...c.enrichment,
                    status: 'done',
                    detailed_view: c.detailed_view,
                    error: null,
                    version: ENRICHMENT_VERSION,
                },
            };
        }

        if (enrichmentHasDetailedView && c.enrichment.status === 'error') {
            return {
                ...normalizedComment,
                enrichment: {
                    ...c.enrichment,
                    status: 'done',
                    error: null,
                    version: ENRICHMENT_VERSION,
                },
            };
        }

        return normalizedComment;
    }
    return {
        ...normalizedComment,
        enrichment: {
            status: hasDetailedView ? 'done' : 'idle',
            detailed_view: hasDetailedView ? c.detailed_view : null,
            error: null,
            enriched_at: null,
            version: ENRICHMENT_VERSION,
        },
    };
}

function deduplicateAuthoritativeComments(comments) {
    const seenIds = new Set();
    return comments.filter((comment) => {
        const id = comment?.id;
        if (!id) return true;
        const key = String(id);
        if (seenIds.has(key)) return false;
        seenIds.add(key);
        return true;
    });
}

function fastGlobalSnapshotKey(comment) {
    const metadata = comment?.metadata_json || comment?.metadataJson || {};
    if (metadata.review_mode !== 'fast_global') return null;
    if (metadata.fast_global_run_id) return `run:${metadata.fast_global_run_id}`;
    const consolidation = metadata.fast_global_consolidation;
    if (consolidation?.consolidation_fingerprint) {
        return `consolidation:${consolidation.consolidation_fingerprint}`;
    }
    if (metadata.consolidation_fingerprint) {
        return `consolidation:${metadata.consolidation_fingerprint}`;
    }
    return metadata.review_id ? `review:${metadata.review_id}` : null;
}

export function currentFastGlobalSnapshot(comments, currentPovFingerprint = null) {
    if (!Array.isArray(comments)) return comments;
    const scopedFastGlobal = comments.filter((comment) => {
        const metadata = comment?.metadata_json || comment?.metadataJson || {};
        return fastGlobalSnapshotKey(comment) !== null && metadata.fast_global_pov_fingerprint;
    });
    if (currentPovFingerprint && scopedFastGlobal.length) {
        comments = comments.filter((comment) => {
            const key = fastGlobalSnapshotKey(comment);
            if (key === null) return true;
            const metadata = comment?.metadata_json || comment?.metadataJson || {};
            return metadata.fast_global_pov_fingerprint === currentPovFingerprint;
        });
    }
    const ranks = new Map();
    comments.forEach((comment, index) => {
        const key = fastGlobalSnapshotKey(comment);
        if (!key) return;
        const metadata = comment?.metadata_json || comment?.metadataJson || {};
        const launchRank = metadata.fast_global_run_started_at || '';
        const persistenceRank = comment?.created_at || '';
        const temporalRank = launchRank || persistenceRank;
        const rank = {hasTime: temporalRank ? 1 : 0, temporalRank, firstIndex: index, key};
        const previous = ranks.get(key);
        if (
            !previous
            || rank.hasTime > previous.hasTime
            || (rank.hasTime === previous.hasTime && rank.temporalRank > previous.temporalRank)
            || (
                rank.hasTime === previous.hasTime
                && rank.temporalRank === previous.temporalRank
                && rank.firstIndex < previous.firstIndex
            )
        ) ranks.set(key, rank);
    });
    const latestKey = [...ranks.entries()]
        .sort((left, right) => (
            right[1].hasTime - left[1].hasTime
            || right[1].temporalRank.localeCompare(left[1].temporalRank)
            || left[1].firstIndex - right[1].firstIndex
            || right[1].key.localeCompare(left[1].key)
        ))[0]?.[0];
    if (!latestKey) return comments;
    const priorVisibleCardIds = new Set(
        comments.flatMap((comment) => {
            if (fastGlobalSnapshotKey(comment) !== latestKey) return [];
            const metadata = comment?.metadata_json || comment?.metadataJson || {};
            return metadata.fast_global_prior_visible_card_ids || [];
        }).filter(Boolean).map(String)
    );

    const isPriorVisible = (comment) => {
        const metadata = comment?.metadata_json || comment?.metadataJson || {};
        const snapshot = metadata.fast_global_card_v1;
        return snapshot && priorVisibleCardIds.has(String(snapshot.card_id || ''));
    };

    return comments.filter((comment) => {
        const key = fastGlobalSnapshotKey(comment);
        return key === null || key === latestKey || isPriorVisible(comment);
    });
}

const createInitialReferenceReviewState = (overrides = {}) => ({
    requirements_by_id: {},
    requirement_order: [],
    verdicts_by_id: {},
    verdict_order: [],
    terminal: null,
    persisted_snapshot: null,
    context: {
        analysis_id: null,
        document_version_id: null,
        review_id: null,
        snapshot_id: null,
        stream_instance_id: null,
        criterion_id: null,
        criterion_name: null,
        reference_document_id: null,
        reference_document_title: null,
    },
    status: 'idle',
    hydration_status: 'idle',
    hydration_error: null,
    active_hydration_request_id: null,
    result_source: 'none',
    accepting_progress: false,
    superseded_review_ids: [],
    ...overrides,
});

const referenceReviewContextFromPayload = (payload = {}) => {
    const normalized = normalizeReferenceReviewSnapshotPayload(payload);
    const scope = getReferenceReviewScope(normalized);
    const metadata = normalized.snapshot_metadata || {};
    const criterion = metadata.criterion || normalized.criterion || {};
    const referenceDocument = {
        ...(metadata.reference_document || {}),
        ...(normalized.reference_document || {}),
    };
    return {
        ...scope,
        criterion_id: normalized.criterion_id || metadata.criterion_id || criterion.id || null,
        criterion_name: normalized.criterion_name
            || normalized.criterion_title
            || metadata.criterion_name
            || metadata.criterion_title
            || criterion.title
            || criterion.name
            || null,
        reference_document_id: normalized.reference_document_id
            || normalized.reference_file_id
            || metadata.reference_document_id
            || metadata.reference_file_id
            || referenceDocument.file_id
            || referenceDocument.id
            || null,
        reference_document_title: normalized.reference_document_title
            || normalized.reference_file_title
            || metadata.reference_document_title
            || metadata.reference_file_title
            || referenceDocument.title
            || referenceDocument.name
            || null,
    };
};

const mergeReferenceReviewContext = (current = {}, payload = {}) => {
    const incoming = referenceReviewContextFromPayload(payload);
    return Object.fromEntries(
        Object.entries({...current, ...incoming}).map(([key, value]) => [
            key,
            incoming[key] === null || incoming[key] === undefined
                ? current[key] ?? null
                : value,
        ])
    );
};

const referenceReviewScopeMatches = (current = {}, payload = {}) => {
    const incoming = getReferenceReviewScope(payload);
    const context = current.context || {};
    if (
        incoming.analysis_id
        && context.analysis_id
        && String(incoming.analysis_id) !== String(context.analysis_id)
    ) return false;
    if (
        incoming.document_version_id
        && context.document_version_id
        && String(incoming.document_version_id) !== String(context.document_version_id)
    ) return false;
    if (
        incoming.review_id
        && context.review_id
        && String(incoming.review_id) !== String(context.review_id)
    ) return false;
    if (
        incoming.stream_instance_id
        && context.stream_instance_id
        && String(incoming.stream_instance_id) !== String(context.stream_instance_id)
    ) return false;
    return true;
};

const referenceReviewDocumentScopeMatches = (current = {}, payload = {}) => {
    const incoming = getReferenceReviewScope(payload);
    const context = current.context || {};
    if (
        incoming.analysis_id
        && context.analysis_id
        && String(incoming.analysis_id) !== String(context.analysis_id)
    ) return false;
    if (
        incoming.document_version_id
        && context.document_version_id
        && String(incoming.document_version_id) !== String(context.document_version_id)
    ) return false;
    return true;
};

const referenceReviewHydrationRequestMatches = (current = {}, payload = {}) => {
    const incomingRequestId = payload?.hydration_request_id || null;
    const activeRequestId = current.active_hydration_request_id || null;
    return activeRequestId
        ? incomingRequestId === activeRequestId
        : !incomingRequestId;
};

const referenceReviewIdIsSuperseded = (current = {}, payload = {}) => {
    const incomingReviewId = getReferenceReviewScope(payload).review_id;
    return !!incomingReviewId
        && (current.superseded_review_ids || []).includes(String(incomingReviewId));
};

const registerFromItems = (items = []) => {
    const byId = {};
    const order = [];
    items.forEach((item) => {
        const id = String(item?.requirement_id || '').trim();
        if (!id) return;
        if (!Object.prototype.hasOwnProperty.call(byId, id)) order.push(id);
        byId[id] = item;
    });
    return {byId, order};
};

const snapshotFromReferenceReviewRegister = ({terminal, requirements, verdicts}) => ({
    ...(terminal || {}),
    requirements: requirements.order.map((id) => requirements.byId[id]),
    verdicts: verdicts.order.map((id) => verdicts.byId[id]),
});

const isFailedReferenceReviewTerminal = (terminal = {}) => {
    const data = terminal?.data && typeof terminal.data === 'object' ? terminal.data : {};
    const result = terminal?.result && typeof terminal.result === 'object' ? terminal.result : {};
    const completionStatus = terminal?.completion_status
        || data.completion_status
        || result.completion_status
        || null;
    const outcomeCode = terminal?.outcome_code
        || data.outcome_code
        || result.outcome_code
        || null;
    return completionStatus === 'failed' || outcomeCode === 'failed';
};

const initialState = {
    currentVersion: null,    // ID du document current
    content: {
        content_html: '',    // Contenu HTML à afficher
        file_content: null,  // Contenu brut du fichier (pour téléchargement)
        file_content_base64: null,
        file_extension: "pdf",
        file_mime_type: "application/pdf",
        extraction_status: null,
        ocr_status: null,
        ocr_user_message: null,
        analysis_allowed: null,
        party_detection_status: "not_started",
        party_detection_attempts: 0,
        party_detection_error: null,
        party_detection_json: null,
        version: '',         // Numéro de version
        title: '',         // Numéro de version
        created_at: null,     // Date de création
        party_comments: [],     //
        id: null,     // Date de création,
    },
    ia_comments: [],
    isLoading: false,
    ia_comments_loading: false,
    ia_streaming: false,
    analysis_reasoning_messages: [],
    analysis_reasoning_text: '',
    analysis_reasoning_status: 'idle',
    provider_summary: null,
    analysis_stream_mode: null,
    fast_global_analysis_started: false,
    fast_global_observations: [],
    fast_global_observation_count: 0,
    fast_global_observation_ids: [],
    fast_global_thinking_items: [],
    fast_global_cards_started: false,
    fast_global_criteria_status: {},
    fast_global_terminal: null,
    reference_review: createInitialReferenceReviewState(),
    summary_cache: {},

};

export const documentVersionReducer = (state = initialState, action) => {
    switch (action.type) {
        case DocumentActionTypes.SET_VERSION_STATUS: {
            const payload = action.payload || {};
            const contentPatch = {};
            if (Object.prototype.hasOwnProperty.call(payload, 'extraction_status')) {
                contentPatch.extraction_status = payload.extraction_status;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'ocr_status')) {
                contentPatch.ocr_status = payload.ocr_status;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'ocr_user_message')) {
                contentPatch.ocr_user_message = payload.ocr_user_message;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'analysis_allowed')) {
                contentPatch.analysis_allowed = payload.analysis_allowed;
            }
            ['party_detection_status', 'party_detection_attempts', 'party_detection_error', 'party_detection_json']
                .forEach((field) => {
                    if (Object.prototype.hasOwnProperty.call(payload, field)) {
                        contentPatch[field] = payload[field];
                    }
                });
            return {
                ...state,
                content: {
                    ...state.content,
                    ...contentPatch,
                }
            };
        }

        case IAStreamActionTypes.PATCH_IA_COMMENTS:
            return {
                ...state,
                ia_comments: state.ia_comments.map(c => {
                    const newC = action.payload.find(x => x.description === c.description);
                    if (!newC) return c;
                    // Préserver l'enrichment existant si le patch ne l'inclut pas
                    return { ...c, ...newC, enrichment: newC.enrichment?.version === ENRICHMENT_VERSION ? newC.enrichment : c.enrichment };
                })
            };

        case IAStreamActionTypes.SET_IA_COMMENTS: {
            const raw = action.payload;
            // The list endpoint already applies the authoritative document/POV and
            // Fast Global snapshot projection. Re-projecting it here hides valid
            // cross-run cards retained by the backend because private lineage ids
            // are intentionally absent from the public response.
            const authoritative = Array.isArray(raw)
                ? deduplicateAuthoritativeComments(raw)
                : raw;
            const normalized = Array.isArray(authoritative)
                ? authoritative.map(normalizeCommentEnrichment)
                : authoritative;
            return {
                ...state,
                ia_comments: Array.isArray(normalized) && normalized.length > 0
                    ? sortByPage(normalized)
                    : normalized
            };
        }

        case IAStreamActionTypes.ADD_IA_COMMENT: {
            const incoming = normalizeCommentEnrichment(action.payload || {});
            const incomingId = incoming?.id;

            if (incomingId) {
                const existingIndex = state.ia_comments.findIndex((c) => c?.id === incomingId);
                if (existingIndex >= 0) {
                    const existing = state.ia_comments[existingIndex];
                    // Logique de priorité pour enrichment : évite de downgrader loading/error → idle
                    const shouldPreserveExisting =
                        existing.enrichment?.version === ENRICHMENT_VERSION &&
                        existing.enrichment?.status === 'done';

                    const shouldMergeIncoming =
                        incoming.enrichment &&
                        incoming.enrichment.version === ENRICHMENT_VERSION;

                    const enrichment = shouldPreserveExisting
                        ? existing.enrichment
                        : shouldMergeIncoming
                            ? {
                                ...existing.enrichment,
                                ...incoming.enrichment,
                            }
                            : existing.enrichment;

                    const merged = {
                        ...existing,
                        ...incoming,
                        enrichment,
                    };
                    const next = [...state.ia_comments];
                    next[existingIndex] = merged;
                    return {
                        ...state,
                        ia_comments: sortByPage(next),
                    };
                }
            }

            const allComments = [...state.ia_comments, incoming];
            return {
                ...state,
                ia_comments: sortByPage(allComments)
            };
        }

        case IAStreamActionTypes.REMOVE_IA_COMMENTS_BY_IDS: {
            const ids = new Set((action.payload || []).map(String));
            if (ids.size === 0) return state;
            return {
                ...state,
                ia_comments: state.ia_comments.filter(
                    (comment) => !ids.has(String(comment?.id || ''))
                ),
            };
        }

        case DocumentActionTypes.SET_CURRENT_VERSION: {
            const versionChanged = String(action.payload || '') !== String(state.currentVersion || '');
            if (!versionChanged) return state;
            return {
                ...state,
                currentVersion: action.payload, // Stocke seulement l'ID
                provider_summary: state.provider_summary?.document_version_id === action.payload
                    ? state.provider_summary
                    : null,
                analysis_stream_mode: null,
                fast_global_observations: [],
                fast_global_observation_count: 0,
                fast_global_observation_ids: [],
                fast_global_thinking_items: [],
                fast_global_cards_started: false,
                fast_global_criteria_status: {},
                reference_review: createInitialReferenceReviewState(),
            };
        }

        case IAStreamActionTypes.RESET_IA_COMMENTS:
            return {
                ...state,
                provider_summary: null,
                ia_comments: [],
                summary_cache: {},
                fast_global_observations: [],
                fast_global_observation_count: 0,
                fast_global_observation_ids: [],
                fast_global_thinking_items: [],
                fast_global_criteria_status: {},
                reference_review: createInitialReferenceReviewState(),
            };

        case IAStreamActionTypes.SET_ID_LOADING_COMMENTS:
            return {...state, ia_comments_loading: action.payload};


        case IAStreamActionTypes.SET_IA_STREAMING:
            return {...state, ia_streaming: action.payload};

        case IAStreamActionTypes.SET_ANALYSIS_STREAM_MODE:
            const structuredProgressMode = ['fast_global', 'comparison', 'reference_review'].includes(action.payload)
                ? action.payload
                : 'legacy';
            return {
                ...state,
                analysis_stream_mode: structuredProgressMode,
                fast_global_observations: structuredProgressMode !== 'legacy'
                    ? (state.fast_global_observations || [])
                    : [],
                fast_global_observation_count: structuredProgressMode !== 'legacy'
                    ? (state.fast_global_observation_count || 0)
                    : 0,
                fast_global_observation_ids: structuredProgressMode !== 'legacy'
                    ? (state.fast_global_observation_ids || [])
                    : [],
                fast_global_cards_started: structuredProgressMode !== 'legacy'
                    ? state.fast_global_cards_started
                    : false,
                fast_global_thinking_items: structuredProgressMode !== 'legacy'
                    ? (state.fast_global_thinking_items || [])
                    : [],
                fast_global_analysis_started: structuredProgressMode !== 'legacy'
                    ? state.fast_global_analysis_started
                    : false,
            };

        case IAStreamActionTypes.RESET_REFERENCE_REVIEW: {
            const current = state.reference_review || createInitialReferenceReviewState();
            const sameScope = referenceReviewDocumentScopeMatches(current, action.payload || {});
            const startsReferenceRun = action.payload?.review_mode === 'reference_review';
            const supersededReviewIds = new Set(
                sameScope ? current.superseded_review_ids || [] : []
            );
            if (sameScope && current.context?.review_id) {
                supersededReviewIds.add(String(current.context.review_id));
            }
            const baseContext = sameScope
                ? current.context
                : createInitialReferenceReviewState().context;
            const nextContext = mergeReferenceReviewContext(
                {
                    ...baseContext,
                    review_id: null,
                    snapshot_id: null,
                },
                action.payload || {},
            );
            return {
                ...state,
                reference_review: createInitialReferenceReviewState({
                    persisted_snapshot: sameScope ? current.persisted_snapshot : null,
                    context: nextContext,
                    status: startsReferenceRun ? 'extracting' : 'idle',
                    hydration_status: startsReferenceRun
                        ? 'idle'
                        : sameScope ? current.hydration_status : 'idle',
                    result_source: startsReferenceRun
                        ? 'stream'
                        : sameScope && current.persisted_snapshot
                            ? 'persisted'
                            : 'none',
                    accepting_progress: startsReferenceRun,
                    superseded_review_ids: [...supersededReviewIds],
                    active_hydration_request_id: startsReferenceRun
                        ? null
                        : current.active_hydration_request_id,
                }),
            };
        }

        case IAStreamActionTypes.SET_REFERENCE_REVIEW_CONTEXT: {
            const current = state.reference_review || createInitialReferenceReviewState();
            if (!referenceReviewScopeMatches(current, action.payload || {})) return state;
            if (referenceReviewIdIsSuperseded(current, action.payload || {})) return state;
            if (current.status === 'failed' && current.accepting_progress === false) return state;
            return {
                ...state,
                reference_review: {
                    ...current,
                    context: mergeReferenceReviewContext(current.context, action.payload || {}),
                    status: action.payload?.review_mode === 'reference_review'
                        && current.status === 'idle'
                        ? 'extracting'
                        : current.status,
                    result_source: action.payload?.review_mode === 'reference_review'
                        ? 'stream'
                        : current.result_source,
                    accepting_progress: action.payload?.review_mode === 'reference_review'
                        ? true
                        : current.accepting_progress,
                    active_hydration_request_id: action.payload?.review_mode === 'reference_review'
                        ? null
                        : current.active_hydration_request_id,
                    hydration_status: action.payload?.review_mode === 'reference_review'
                        ? 'idle'
                        : current.hydration_status,
                },
            };
        }

        case IAStreamActionTypes.INTERRUPT_REFERENCE_REVIEW: {
            const current = state.reference_review || createInitialReferenceReviewState();
            if (!referenceReviewScopeMatches(current, action.payload || {})) return state;
            if (referenceReviewIdIsSuperseded(current, action.payload || {})) return state;
            if (action.payload?.unidentified_run && current.context?.review_id) return state;
            return {
                ...state,
                reference_review: {
                    ...current,
                    requirements_by_id: {},
                    requirement_order: [],
                    verdicts_by_id: {},
                    verdict_order: [],
                    terminal: null,
                    status: 'failed',
                    result_source: current.persisted_snapshot ? 'persisted' : 'none',
                    accepting_progress: false,
                },
            };
        }

        case IAStreamActionTypes.BEGIN_REFERENCE_REVIEW_HYDRATION: {
            const current = state.reference_review || createInitialReferenceReviewState();
            const incomingContext = referenceReviewContextFromPayload(action.payload || {});
            const sameScope = referenceReviewDocumentScopeMatches(current, action.payload || {});
            const hasCurrentScope = !!(
                current.context?.analysis_id || current.context?.document_version_id
            );
            if (sameScope || !hasCurrentScope) {
                return {
                    ...state,
                    reference_review: {
                        ...current,
                        context: mergeReferenceReviewContext(current.context, action.payload || {}),
                        hydration_status: 'loading',
                        hydration_error: null,
                        active_hydration_request_id: action.payload?.hydration_request_id || null,
                    },
                };
            }
            return {
                ...state,
                analysis_stream_mode: state.analysis_stream_mode === 'reference_review'
                    ? null
                    : state.analysis_stream_mode,
                reference_review: createInitialReferenceReviewState({
                    context: incomingContext,
                    hydration_status: 'loading',
                    active_hydration_request_id: action.payload?.hydration_request_id || null,
                }),
            };
        }

        case IAStreamActionTypes.SET_REFERENCE_REVIEW_UNAVAILABLE: {
            const current = state.reference_review || createInitialReferenceReviewState();
            if (
                !referenceReviewDocumentScopeMatches(current, action.payload || {})
                || !referenceReviewHydrationRequestMatches(current, action.payload || {})
            ) return state;
            const isFlushMarker = action.payload?.reason === "flush";
            const mergedContext = mergeReferenceReviewContext(current.context, action.payload || {});
            const nextHydrationError = action.payload?.reason || null;
            const nextHydrationStatus = action.payload?.incompatible ? "incompatible" : "absent";

            if (isFlushMarker) {
                return {
                    ...state,
                    reference_review: {
                        ...createInitialReferenceReviewState(),
                        context: mergedContext,
                        hydration_status: nextHydrationStatus,
                        hydration_error: nextHydrationError,
                        persisted_snapshot: null,
                        active_hydration_request_id: null,
                        result_source: "none",
                    },
                };
            }
            return {
                ...state,
                reference_review: {
                    ...current,
                    context: mergedContext,
                    persisted_snapshot: current.persisted_snapshot,
                    hydration_status: nextHydrationStatus,
                    hydration_error: nextHydrationError,
                    active_hydration_request_id: null,
                },
            };
        }

        case IAStreamActionTypes.SET_REFERENCE_REVIEW_HYDRATION_ERROR: {
            const current = state.reference_review || createInitialReferenceReviewState();
            if (
                !referenceReviewDocumentScopeMatches(current, action.payload || {})
                || !referenceReviewHydrationRequestMatches(current, action.payload || {})
            ) return state;
            return {
                ...state,
                reference_review: {
                    ...current,
                    context: mergeReferenceReviewContext(current.context, action.payload || {}),
                    hydration_status: 'error',
                    hydration_error: action.payload?.reason || 'reference_review_snapshot_unavailable',
                    active_hydration_request_id: null,
                },
            };
        }

        case IAStreamActionTypes.UPSERT_REFERENCE_REQUIREMENT: {
            const requirement = action.payload?.requirement;
            const id = requirement?.requirement_id;
            if (!id || state.analysis_stream_mode !== 'reference_review') return state;
            const current = state.reference_review || createInitialReferenceReviewState();
            if (
                current.accepting_progress !== true
                || !referenceReviewScopeMatches(current, action.payload || {})
                || referenceReviewIdIsSuperseded(current, action.payload || {})
            ) return state;
            return {
                ...state,
                reference_review: {
                    ...current,
                    context: mergeReferenceReviewContext(current.context, action.payload || {}),
                    requirements_by_id: { ...current.requirements_by_id, [id]: requirement },
                    requirement_order: current.requirement_order.includes(id)
                        ? current.requirement_order
                        : [...current.requirement_order, id],
                    status: 'extracting',
                    result_source: 'stream',
                },
            };
        }

        case IAStreamActionTypes.UPSERT_REFERENCE_VERDICT: {
            const verdict = action.payload?.verdict;
            const id = verdict?.requirement_id;
            if (!id || state.analysis_stream_mode !== 'reference_review') return state;
            const current = state.reference_review || createInitialReferenceReviewState();
            if (
                current.accepting_progress !== true
                || !referenceReviewScopeMatches(current, action.payload || {})
                || referenceReviewIdIsSuperseded(current, action.payload || {})
            ) return state;
            return {
                ...state,
                reference_review: {
                    ...current,
                    context: mergeReferenceReviewContext(current.context, action.payload || {}),
                    verdicts_by_id: { ...current.verdicts_by_id, [id]: verdict },
                    verdict_order: current.verdict_order.includes(id)
                        ? current.verdict_order
                        : [...current.verdict_order, id],
                    status: 'verifying',
                    result_source: 'stream',
                },
            };
        }

        case IAStreamActionTypes.SET_REFERENCE_REVIEW_TERMINAL: {
            const terminal = action.payload || null;
            const current = state.reference_review || createInitialReferenceReviewState();
            if (
                !terminal
                || current.accepting_progress !== true
                || !referenceReviewScopeMatches(current, terminal)
                || referenceReviewIdIsSuperseded(current, terminal)
            ) return state;
            if (isFailedReferenceReviewTerminal(terminal)) {
                const requirements = Array.isArray(terminal.requirements)
                    ? registerFromItems(terminal.requirements)
                    : {
                        byId: current.requirements_by_id,
                        order: current.requirement_order,
                    };
                return {
                    ...state,
                    reference_review: {
                        ...current,
                        // Requirements are independently grounded in the
                        // reference document. Keep them visible when the later
                        // contract compilation/verification phase fails, while
                        // continuing to fail closed for every provisional verdict.
                        requirements_by_id: requirements.byId,
                        requirement_order: requirements.order,
                        verdicts_by_id: {},
                        verdict_order: [],
                        terminal: {
                            ...terminal,
                            completion_status: 'failed',
                            snapshot_persisted: false,
                            requirements: requirements.order.map(
                                (id) => requirements.byId[id]
                            ),
                            verdicts: [],
                        },
                        context: mergeReferenceReviewContext(current.context, terminal),
                        status: 'failed',
                        result_source: current.persisted_snapshot
                            ? 'persisted'
                            : requirements.order.length > 0
                                ? 'stream'
                                : 'none',
                        accepting_progress: false,
                    },
                };
            }
            const requirements = Array.isArray(terminal.requirements)
                ? registerFromItems(terminal.requirements)
                : {
                    byId: current.requirements_by_id,
                    order: current.requirement_order,
                };
            const verdicts = Array.isArray(terminal.verdicts)
                ? registerFromItems(terminal.verdicts)
                : {
                    byId: current.verdicts_by_id,
                    order: current.verdict_order,
                };
            const reconciled = snapshotFromReferenceReviewRegister({
                terminal,
                requirements,
                verdicts,
            });
            const persisted = terminal.snapshot_persisted === true;
            return {
                ...state,
                reference_review: {
                    ...current,
                    requirements_by_id: requirements.byId,
                    requirement_order: requirements.order,
                    verdicts_by_id: verdicts.byId,
                    verdict_order: verdicts.order,
                    terminal: reconciled,
                    persisted_snapshot: persisted ? reconciled : current.persisted_snapshot,
                    context: mergeReferenceReviewContext(current.context, terminal),
                    status: terminal?.completion_status || 'failed',
                    result_source: 'stream',
                    accepting_progress: false,
                },
            };
        }

        case IAStreamActionTypes.HYDRATE_REFERENCE_REVIEW_SNAPSHOT: {
            const current = state.reference_review || createInitialReferenceReviewState();
            if (
                !referenceReviewDocumentScopeMatches(current, action.payload || {})
                || !referenceReviewHydrationRequestMatches(current, action.payload || {})
            ) return state;
            const snapshot = normalizeReferenceReviewSnapshotPayload(action.payload);
            if (!Array.isArray(snapshot.requirements) || !Array.isArray(snapshot.verdicts)) return state;
            const requirements = registerFromItems(snapshot.requirements);
            const verdicts = registerFromItems(snapshot.verdicts);
            const canonicalSnapshot = snapshotFromReferenceReviewRegister({
                terminal: snapshot,
                requirements,
                verdicts,
            });
            return {
                ...state,
                analysis_stream_mode: 'reference_review',
                reference_review: {
                    ...createInitialReferenceReviewState(),
                    requirements_by_id: requirements.byId,
                    requirement_order: requirements.order,
                    verdicts_by_id: verdicts.byId,
                    verdict_order: verdicts.order,
                    terminal: canonicalSnapshot,
                    persisted_snapshot: canonicalSnapshot,
                    context: mergeReferenceReviewContext(current.context, snapshot),
                    status: snapshot.completion_status,
                    hydration_status: 'loaded',
                    result_source: 'persisted',
                    accepting_progress: false,
                    active_hydration_request_id: null,
                },
            };
        }

        case IAStreamActionTypes.START_FAST_GLOBAL_ANALYSIS:
            if (state.analysis_stream_mode !== 'fast_global') return state;
            return {
                ...state,
                fast_global_analysis_started: true,
                fast_global_terminal: null,
            };

        case IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS: {
            const payload = action.payload || {};
            const criterionId = String(payload.criterion_id || '');
            if (!criterionId) return state;
            return {
                ...state,
                fast_global_criteria_status: {
                    ...(state.fast_global_criteria_status || {}),
                    [criterionId]: {
                        ...(state.fast_global_criteria_status?.[criterionId] || {}),
                        ...payload,
                        criterion_id: criterionId,
                    },
                },
            };
        }

        case IAStreamActionTypes.SET_FAST_GLOBAL_TERMINAL:
            return {
                ...state,
                fast_global_terminal: action.payload || null,
            };

        case IAStreamActionTypes.ADD_FAST_GLOBAL_OBSERVATION: {
            const observation = normalizeFastGlobalObservation(action.payload);
            const previousObservations = state.fast_global_observations || [];
            const seenObservationIds = state.fast_global_observation_ids || [];
            if (
                !['fast_global', 'comparison'].includes(state.analysis_stream_mode)
                || state.fast_global_cards_started
                || !observation
                || seenObservationIds.includes(observation.observation_id)
            ) {
                return state;
            }
            return {
                ...state,
                fast_global_observations: [
                    ...previousObservations,
                    observation,
                ],
                fast_global_observation_count: (state.fast_global_observation_count || 0) + 1,
                fast_global_observation_ids: [
                    ...seenObservationIds,
                    observation.observation_id,
                ],
            };
        }

        case IAStreamActionTypes.UPSERT_FAST_GLOBAL_OBSERVATION: {
            const observation = normalizeFastGlobalObservation(action.payload);
            if (
                state.analysis_stream_mode !== 'comparison'
                || state.fast_global_cards_started
                || !observation
            ) return state;
            const previous = state.fast_global_observations || [];
            const existingIndex = previous.findIndex(
                (item) => item.observation_id === observation.observation_id
            );
            if (existingIndex < 0) {
                return {
                    ...state,
                    fast_global_observations: [...previous, observation],
                    fast_global_observation_count: (state.fast_global_observation_count || 0) + 1,
                    fast_global_observation_ids: [
                        ...(state.fast_global_observation_ids || []),
                        observation.observation_id,
                    ],
                };
            }
            const merged = {
                ...previous[existingIndex],
                ...observation,
                comparison_detected_snapshot:
                    previous[existingIndex].comparison_detected_snapshot
                    || (
                        previous[existingIndex].comparison_state === 'detected'
                            ? previous[existingIndex]
                            : null
                    ),
            };
            return {
                ...state,
                fast_global_observations: [
                    ...previous.slice(0, existingIndex),
                    ...previous.slice(existingIndex + 1),
                    merged,
                ],
            };
        }

        case IAStreamActionTypes.DISCARD_COMPARISON_PREVIEWS: {
            if (state.analysis_stream_mode !== 'comparison') return state;
            const retained = (state.fast_global_observations || []).reduce((items, item) => {
                if (item.comparison_state !== 'assessment_preview') return [...items, item];
                if (!item.comparison_detected_snapshot) return items;
                const {
                    comparison_detected_snapshot: _discardedSnapshot,
                    ...detected
                } = item.comparison_detected_snapshot;
                return [...items, detected];
            }, []);
            return {
                ...state,
                fast_global_observations: retained,
                fast_global_observation_count: retained.length,
                fast_global_observation_ids: retained.map((item) => item.observation_id),
            };
        }

        case IAStreamActionTypes.DISCARD_COMPARISON_CARD_PREVIEWS:
            if (state.analysis_stream_mode !== 'comparison') return state;
            return {
                ...state,
                ia_comments: state.ia_comments.filter(
                    (comment) => comment?.comparison_provisional !== true
                ),
                fast_global_cards_started: action.payload?.resetCardsStarted === true
                    ? false
                    : state.fast_global_cards_started,
            };

        case IAStreamActionTypes.UPSERT_FAST_GLOBAL_THINKING: {
            const thinking = normalizeFastGlobalThinking(action.payload);
            if (
                state.analysis_stream_mode !== 'fast_global'
                || state.fast_global_cards_started
                || !thinking
            ) return state;
            const previous = state.fast_global_thinking_items || [];
            const existingIndex = previous.findIndex(
                (item) => item.thinking_id === thinking.thinking_id
            );
            if (
                existingIndex >= 0
                && previous[existingIndex].message.length > thinking.message.length
            ) return state;
            const next = [...previous];
            if (existingIndex >= 0) next[existingIndex] = thinking;
            else next.push(thinking);
            return {
                ...state,
                fast_global_thinking_items: next.slice(-12),
            };
        }

        case IAStreamActionTypes.START_FAST_GLOBAL_CARDS:
            if (!['fast_global', 'comparison'].includes(state.analysis_stream_mode)) return state;
            return {
                ...state,
                fast_global_observations: [],
                fast_global_observation_count: 0,
                fast_global_observation_ids: [],
                fast_global_thinking_items: [],
                fast_global_cards_started: true,
            };

        case IAStreamActionTypes.CLEAR_FAST_GLOBAL_OBSERVATIONS:
            return {
                ...state,
                fast_global_observations: [],
                fast_global_observation_count: 0,
                fast_global_observation_ids: [],
                fast_global_thinking_items: [],
            };

        case IAStreamActionTypes.RESET_FAST_GLOBAL_STREAM:
            return {
                ...state,
                provider_summary: null,
                analysis_stream_mode: null,
                fast_global_observations: [],
                fast_global_observation_count: 0,
                fast_global_observation_ids: [],
                fast_global_thinking_items: [],
                fast_global_cards_started: false,
                fast_global_criteria_status: {},
                fast_global_terminal: null,
                fast_global_analysis_started: false,
            };

        case IAStreamActionTypes.RESET_ANALYSIS_REASONING:
            return {
                ...state,
                provider_summary: null,
                analysis_reasoning_messages: [],
                analysis_reasoning_text: '',
                analysis_reasoning_status: 'idle',
            };

        case IAStreamActionTypes.RESET_PROVIDER_SUMMARY:
            return {
                ...state,
                provider_summary: {
                    analysis_id: action.payload?.analysis_id,
                    document_version_id: action.payload?.document_version_id,
                    stream_instance_id: action.payload?.stream_instance_id,
                    text: '',
                    sequences_by_criterion: {},
                    status: 'active',
                },
            };

        case IAStreamActionTypes.APPEND_PROVIDER_SUMMARY: {
            const payload = action.payload || {};
            const summary = state.provider_summary;
            if (
                !summary || summary.status !== 'active' || state.fast_global_cards_started
                || state.analysis_stream_mode !== 'fast_global'
                || payload.event !== 'analysis_activity_note'
                || payload.schema_version !== 'fast_global_event_v1'
                || typeof payload.criterion_id !== 'string' || !payload.criterion_id
                || payload.analysis_id !== summary.analysis_id
                || payload.stream_instance_id !== summary.stream_instance_id
                || !Number.isInteger(payload.sequence) || payload.sequence < 0
                || payload.sequence <= (summary.sequences_by_criterion[payload.criterion_id] ?? -1)
                || typeof payload.message !== 'string' || !payload.message.trim() || payload.message.length > 420
            ) return state;
            return {
                ...state,
                provider_summary: {
                    ...summary,
                    text: payload.message,
                    sequences_by_criterion: {
                        ...summary.sequences_by_criterion,
                        [payload.criterion_id]: payload.sequence,
                    },
                },
            };
        }

        case IAStreamActionTypes.UPDATE_BATCH_ACTIVITY: {
            const payload = action.payload || {};
            const summary = state.provider_summary;
            if (!summary || summary.status !== 'active' || state.fast_global_cards_started
                || state.analysis_stream_mode !== 'fast_global'
                || payload.schema_version !== FAST_GLOBAL_BATCH_EVENT_SCHEMA
                || payload.analysis_id !== summary.analysis_id
                || payload.stream_instance_id !== summary.stream_instance_id
                || typeof payload.batch_id !== 'string' || !payload.batch_id
                || (summary.batch_activity?.batch_id && summary.batch_activity.batch_id !== payload.batch_id)) return state;
            const phase = payload.event === 'cross_criterion_consolidation_started' ? 'consolidation'
                : payload.event === 'analysis_started' || payload.event === 'analysis_mechanism_progress' ? 'reading'
                : ['criterion_started', 'criterion_cache_hit', 'criterion_completed', 'criterion_failed', 'criterion_error'].includes(payload.event) ? 'criteria' : null;
            if (!phase) return state;
            return { ...state, provider_summary: { ...summary, text: '', batch_activity: {
                batch_id: payload.batch_id, phase, source_event: payload.event, reading_status: payload.status,
                topic: payload.status === 'item_completed' && typeof payload.title === 'string'
                    ? payload.title.replace(/\s+/g, ' ').trim() : '',
            } } };
        }

        case IAStreamActionTypes.SET_PROVIDER_SUMMARY_STATUS:
            if (!state.provider_summary
                || !['completed', 'failed'].includes(action.payload?.status)
                || action.payload?.stream_instance_id !== state.provider_summary.stream_instance_id) return state;
            return {
                ...state,
                provider_summary: {
                    ...state.provider_summary,
                    status: action.payload.status,
                },
            };

        case IAStreamActionTypes.ADD_ANALYSIS_REASONING: {
            const rawPayload = action.payload || {};
            const message = normalizeAnalysisReasoningMessage(
                rawPayload.message || action.payload,
                rawPayload.display_language
            );
            if (!message) return state;
            const previous = state.analysis_reasoning_messages || [];
            const previousText = state.analysis_reasoning_text || '';
            // Avoid exact duplicates back-to-back (Bedrock can re-emit the same delta)
            if (previous[previous.length - 1] === message) return state;
            // UX progress messages are intentionally compact; avoid repeating
            // the same phase later in the same analysis.
            if (previous.includes(message)) return state;

            // Legacy stitching kept for compatibility, then normalized below.
            let nextText;
            if (!previousText) {
                nextText = message;
            } else {
                const endsWithSpace = /\s$/.test(previousText);
                const endsWithPunct = /[.!?…:;,]$/.test(previousText);
                const startsWithPunct = /^[.!?…:;,)\]]/.test(message);
                const startsWithSpace = /^\s/.test(message);
                let joiner = '';
                if (!endsWithSpace && !startsWithSpace && !startsWithPunct) {
                    // If previous ends with punctuation (sentence break), add space.
                    // Otherwise stitch directly to avoid breaking words.
                    joiner = endsWithPunct ? ' ' : '';
                }
                nextText = previousText + joiner + message;
            }

            const nextMessages = [...previous, message].slice(-ANALYSIS_REASONING_MAX_MESSAGES);
            nextText = nextMessages.join('\n');

            return {
                ...state,
                analysis_reasoning_messages: nextMessages,
                analysis_reasoning_text: nextText,
                analysis_reasoning_status: 'active',
            };
        }

        case IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS:
            return {
                ...state,
                analysis_reasoning_status: action.payload || 'idle',
            };

        case IAStreamActionTypes.UPSERT_SUMMARY_CACHE_ENTRY: {
            const {key, entry} = action.payload || {};
            if (!key || !entry || typeof entry !== 'object') return state;
            return {
                ...state,
                summary_cache: {
                    ...(state.summary_cache || {}),
                    [key]: {
                        ...((state.summary_cache || {})[key] || {}),
                        ...entry,
                    },
                },
            };
        }

        case IAStreamActionTypes.CLEAR_SUMMARY_CACHE:
            return {
                ...state,
                summary_cache: {},
            };

        case DocumentActionTypes.SET_VERSION_CONTENT:
            {
                const incoming = action.payload || {};
                return {
                    ...state,
                    content: {
                        ...state.content,
                        ...incoming,
                    }
                };
            }

        case DocumentActionTypes.SET_VERSION_TITLE:
            if (String(state.currentVersion) !== String(action.payload.id)) {
                return state;
            }
            return {
                ...state,
                content: {
                    ...state.content,
                    title: action.payload.title
                }
            };

        case DocumentActionTypes.SET_VERSION_LOADING:
            return {
                ...state,
                isLoading: action.payload
            };

        case IAStreamActionTypes.FLUSH_IA_COMMENTS:
            return {
                ...state,
                provider_summary: null,
                ia_comments: [],
                summary_cache: {},
                analysis_stream_mode: null,
                fast_global_observations: [],
                fast_global_observation_count: 0,
                fast_global_observation_ids: [],
                fast_global_thinking_items: [],
                fast_global_cards_started: false,
                fast_global_analysis_started: false,
                reference_review: createInitialReferenceReviewState(),
            };
        // ── PR#3.4.1 : patch ciblé du champ enrichment d'un commentaire ─────
        case IAStreamActionTypes.PATCH_COMMENT_ENRICHMENT: {
            const { id, enrichment } = action.payload;
            return {
                ...state,
                ia_comments: state.ia_comments.map((c) =>
                    String(c.id) === String(id)
                        ? {
                            ...c,
                            enrichment: {
                                ...(c.enrichment || {}),
                                ...enrichment,
                                version: ENRICHMENT_VERSION,
                            },
                          }
                        : c
                ),
            };
        }

        default:
            return state;
    }
};
